import { DatePickerArrowButtonType, DatePickerWithArrows } from 'components/molecules';
import { AppButton, AppFlex } from 'components/atoms';
import { commonTexts, settlementTexts } from 'consts/text';
import { FULL_MONTH_FORMAT } from 'consts/dateFormats';
import dayjs, { Dayjs } from 'dayjs';
import { ChildId, ChildSettlementId } from 'types';
import { useAppMutation } from 'services/reactQuery/useAppMutation';
import { useSimpleNotification } from 'hooks/useSimpleNotification/useSimpleNotification';
import StaffHTTPService from 'services/HTTPService/staff/StaffHTTPService';
import { IconCheck, IconFileSettings, IconNotes, IconPlus, IconRefresh } from '@tabler/icons-react';
import { useIsMobile } from 'hooks/useIsMobile/useIsMobile';
import {
    VerifySettlementsModal,
    VerifySettlementsModalMode,
} from 'components/templates/settlement/VerifySettlementsModal';
import { settlementsVerifyErrorParser } from 'utils/errorHandlers/settlement/settlementsVerifyErrorParser';
import { AxiosError } from 'axios';
import styled from 'styled-components';
import { Row } from 'antd';
import { useMemo, useState } from 'react';
import { useAppQuery } from 'services/reactQuery/useAppQuery';
import { useToggle } from 'hooks/useToggle/useToggle';

import { If } from '../../../../atoms/If/If';
import { ChildSettlementDetailsTabValue } from '../ChildSettlementDetails.utils';
import { AddEditAdditionalCostModal } from '../../AddEditAdditionalCostModal/AddEditAdditionalCostModal';

const FiltersContainer = styled(Row)`
    margin-top: 32px;
    margin-bottom: 32px;
`;

type Props = {
    settlementType: ChildSettlementDetailsTabValue;
    childId: ChildId;
    selectedDate: Dayjs;
    selectedYear: number;
    selectedMonth: number;
    handleChangeDateFilter: (selectedDate: dayjs.Dayjs | null) => void;
    handleClickNextOrPrevDate: (
        selectedDate: DatePickerArrowButtonType,
        unit?: 'month' | 'week' | 'day' | undefined,
    ) => void;
};

export const ChildSettlementsSectionHeader = ({
    settlementType,
    childId,
    selectedMonth,
    selectedYear,
    selectedDate,
    handleChangeDateFilter,
    handleClickNextOrPrevDate,
}: Props) => {
    const [isManualBillOpen, setIsManualBillOpen] = useState(false);

    const { displaySuccess, displayError } = useSimpleNotification();
    const isMobile = useIsMobile();

    const [verifySettlementModalOpen, openVerifyModal, closeVerifyModal] = useToggle(false);

    const yearMonth = `${selectedYear}-${selectedMonth.toString().padStart(2, '0')}`;

    const { data: settlementActiveDate } = useAppQuery(
        'IS_SETTLEMENT_ACTIVE',
        [childId, yearMonth],
        () =>
            StaffHTTPService.settlements.getIsChildSettlementActive({
                childId,
                yearMonth,
            }),
        {
            refetchOnWindowFocus: false,
        },
    );

    const { data: childSettlement } = useAppQuery(
        'CHILD_SETTLEMENT',
        [childId, yearMonth],
        () =>
            StaffHTTPService.settlements.getChildSettlement({
                childId,
                period: yearMonth,
            }),
        {},
    );

    const { mutate: recalculateSettlement, isLoading: isLoadingRecalculateSettlement } =
        useAppMutation(StaffHTTPService.settlements.postRecalculateSettlement, {
            key: ['SETTLEMENT_RECALCULATE'],
            onSuccess: () =>
                displaySuccess(
                    settlementTexts.children.recalculate.successfullyRecalculatedSettlementMessage,
                ),
            onError: () => displayError(commonTexts.errorMessages.actionExecution),
            invalidateQueryKeys: [
                ['SETTLEMENTS'],
                ['CHILD_SETTLEMENT_BALANCE'],
                ['CHILD_SETTLEMENT'],
                ['CHILD_SETTLEMENT_PAYMENT'],
                ['VERIFICATION_STATUS', selectedMonth, selectedYear],
            ],
        });

    const { mutate: verifyChildSettlement, isLoading: isVerifyChildSettlementLoading } =
        useAppMutation(StaffHTTPService.settlements.verifyChildSettlement, {
            key: ['VERIFY_CHILD_SETTLEMENT_V2'],
            onSuccess: () => {
                closeVerifyModal();
                displaySuccess(
                    settlementTexts.children.verifySettlements.singleSettlement
                        .verifySettlementSuccessMessage,
                );
            },
            onError: (error: AxiosError) =>
                displayError(
                    settlementsVerifyErrorParser(error, commonTexts.errorMessages.actionExecution),
                ),
            invalidateQueryKeys: [
                ['SETTLEMENTS'],
                ['CHILD_SETTLEMENT_BALANCE'],
                ['CHILD_SETTLEMENT'],
                ['CHILD_SETTLEMENT_PAYMENT'],
                ['VERIFICATION_STATUS', selectedMonth, selectedYear],
            ],
        });

    const { mutate: verifyChildCorrection, isLoading: isVerifyChildCorrectionLoading } =
        useAppMutation(StaffHTTPService.settlements.verifyChildCorrection, {
            key: ['VERIFY_CHILD_CORRECTION'],
            onSuccess: () => {
                closeVerifyModal();
                displaySuccess(
                    settlementTexts.children.verifySettlements.singleSettlement
                        .verifySettlementSuccessMessage,
                );
            },
            onError: (error: AxiosError) =>
                displayError(
                    settlementsVerifyErrorParser(error, commonTexts.errorMessages.actionExecution),
                ),
            invalidateQueryKeys: [
                ['SETTLEMENTS'],
                ['CHILD_SETTLEMENT_BALANCE'],
                ['CHILD_SETTLEMENT'],
                ['CHILD_SETTLEMENT_PAYMENT'],
                ['VERIFICATION_STATUS', selectedMonth, selectedYear],
            ],
        });

    const {
        mutate: addOrCalculateChildCorrection,
        isLoading: isLoadingAddOrCalculateChildCorrection,
    } = useAppMutation(StaffHTTPService.settlements.addOrCalculateChildCorrection, {
        key: ['ADD_MONTHLY_SETTLEMENT_CORRECTION'],
        onSuccess: () => {
            closeVerifyModal();
            displaySuccess(
                settlementTexts.children.verifySettlements.singleSettlement
                    .verifySettlementSuccessMessage,
            );
        },
        onError: (error: AxiosError) =>
            displayError(
                settlementsVerifyErrorParser(error, commonTexts.errorMessages.actionExecution),
            ),
        invalidateQueryKeys: [
            ['SETTLEMENTS'],
            ['CHILD_SETTLEMENT_BALANCE'],
            ['CHILD_SETTLEMENT'],
            ['CHILD_SETTLEMENT_PAYMENT'],
            ['VERIFICATION_STATUS', selectedMonth, selectedYear],
        ],
    });

    const isMainSettlementVerified = !!childSettlement?.monthlySettlementDetailsDto.verified;
    const isMainSettlementComputed = !!childSettlement?.monthlySettlementDetailsDto.computed;
    const areCorrectionsPresent = !!childSettlement?.corrections?.length;
    const isLastCorrectionVerified = !!childSettlement?.corrections.at(0)?.verified;
    const lastCorrectionId = childSettlement?.corrections.at(0)?.id;

    const onRecalculateSettlementClick = async () => {
        recalculateSettlement({
            childId: childId as ChildId,
            period: yearMonth,
        });
    };

    const settlementId = useMemo(
        () => childSettlement?.monthlySettlementDetailsDto.id,
        [childSettlement],
    ) as ChildSettlementId | undefined;

    const onVerifySettlementClick = async () => {
        try {
            if (settlementId) {
                verifyChildSettlement(settlementId);
            }
        } catch (error) {
            displayError(commonTexts.errorMessages.actionExecution);
        }
    };

    const onVerifyCorrectionClick = async () => {
        try {
            if (lastCorrectionId) {
                verifyChildCorrection(lastCorrectionId);
            }
        } catch (error) {
            displayError(commonTexts.errorMessages.actionExecution);
        }
    };

    const onAddOrCalculateMonthlySettlementCorrectionClick = async () => {
        try {
            if (settlementId) {
                addOrCalculateChildCorrection(settlementId);
            }
        } catch (error) {
            displayError(commonTexts.errorMessages.actionExecution);
        }
    };

    return (
        <>
            <VerifySettlementsModal
                mode={VerifySettlementsModalMode.ONE_SETTLEMENT}
                monthNumber={selectedMonth - 1}
                open={verifySettlementModalOpen}
                confirmActionLoading={isVerifyChildSettlementLoading}
                onOk={onVerifySettlementClick}
                onCancel={closeVerifyModal}
            />
            <AddEditAdditionalCostModal
                isOpen={isManualBillOpen}
                mode="add"
                childId={childId}
                onCancel={() => setIsManualBillOpen(false)}
            />
            <FiltersContainer justify="space-between" align="bottom">
                <DatePickerWithArrows
                    label={commonTexts.actionLabels.selectMonth}
                    value={selectedDate}
                    picker="month"
                    format={FULL_MONTH_FORMAT}
                    onChange={handleChangeDateFilter}
                    onArrowButtonClick={(arrowButtonType) =>
                        handleClickNextOrPrevDate(arrowButtonType, 'month')
                    }
                />
                <AppFlex align="center" gap="small" vertical={isMobile} flex={isMobile ? 1 : 0}>
                    <If condition={settlementType === 'MANUAL_SETTLEMENT'}>
                        <AppButton
                            htmlType="button"
                            type="default"
                            margin={0}
                            fullWidth={isMobile}
                            icon={<IconPlus />}
                            loading={isLoadingRecalculateSettlement}
                            onClick={(e) => {
                                if (e && typeof e.preventDefault === 'function') {
                                    e.preventDefault();
                                }
                                setIsManualBillOpen(true);
                            }}
                        >
                            {settlementTexts.children.details.addBill}
                        </AppButton>
                    </If>

                    <If condition={settlementActiveDate?.active}>
                        <If condition={settlementType === 'MONTHLY_SETTLEMENT'}>
                            <If condition={!areCorrectionsPresent}>
                                <If condition={!isMainSettlementVerified}>
                                    <AppButton
                                        type="primary"
                                        margin={0}
                                        fullWidth={isMobile}
                                        icon={<IconRefresh />}
                                        loading={isLoadingRecalculateSettlement}
                                        onClick={onRecalculateSettlementClick}
                                    >
                                        {settlementTexts.children.details.recalculateBill}
                                    </AppButton>
                                    <If condition={isMainSettlementComputed}>
                                        <AppButton
                                            type="primary"
                                            margin={0}
                                            fullWidth={isMobile}
                                            icon={<IconCheck />}
                                            disabled={isLoadingRecalculateSettlement}
                                            onClick={openVerifyModal}
                                        >
                                            {settlementTexts.children.details.verify}
                                        </AppButton>
                                    </If>
                                </If>
                                <If condition={isMainSettlementVerified}>
                                    <AppButton
                                        margin={0}
                                        fullWidth={isMobile}
                                        icon={<IconNotes />}
                                        disabled={isLoadingAddOrCalculateChildCorrection}
                                        onClick={onAddOrCalculateMonthlySettlementCorrectionClick}
                                    >
                                        {settlementTexts.children.details.addCorrection}
                                    </AppButton>
                                </If>
                            </If>
                            <If condition={areCorrectionsPresent}>
                                <If condition={!isLastCorrectionVerified}>
                                    <AppButton
                                        type="primary"
                                        margin={0}
                                        fullWidth={isMobile}
                                        icon={<IconRefresh />}
                                        disabled={isLoadingAddOrCalculateChildCorrection}
                                        onClick={onAddOrCalculateMonthlySettlementCorrectionClick}
                                    >
                                        {settlementTexts.children.details.recalculateCorrection}
                                    </AppButton>
                                    <AppButton
                                        type="primary"
                                        margin={0}
                                        fullWidth={isMobile}
                                        icon={<IconFileSettings />}
                                        disabled={isVerifyChildCorrectionLoading}
                                        onClick={onVerifyCorrectionClick}
                                    >
                                        {settlementTexts.children.details.verify}
                                    </AppButton>
                                </If>
                                <If condition={isLastCorrectionVerified}>
                                    <AppButton
                                        margin={0}
                                        fullWidth={isMobile}
                                        icon={<IconNotes />}
                                        disabled={isLoadingAddOrCalculateChildCorrection}
                                        onClick={onAddOrCalculateMonthlySettlementCorrectionClick}
                                    >
                                        {settlementTexts.children.details.createCorrection}
                                    </AppButton>
                                </If>
                            </If>
                        </If>
                    </If>
                </AppFlex>
            </FiltersContainer>
        </>
    );
};
